import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Link,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  CreateNewFolder as CreateNewFolderIcon,
  Home as HomeIcon,
  NoteAdd as NoteAddIcon,
  Refresh as RefreshIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

import FileGrid, { FileItem, isDirectory } from "./FileGrid";
import MultiSelectToolbar from "./MultiSelectToolbar";
import UploadDrawer, { UploadFab } from "./UploadDrawer";
import TextPadDrawer from "./TextPadDrawer";
import {
  copyPaste,
  createFolder,
  fetchPath,
  invalidatePath,
} from "./app/transfer";
import { useTransferQueue, useUploadEnqueue } from "./app/transferQueue";
import { authHeaders, canWritePath, fileUrl, Session } from "./app/api";
import { joinKey, validName } from "./app/utils";

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
      }}
    >
      {children}
    </Box>
  );
}

function EmptyState({
  canWrite,
  onUpload,
  onCreateFolder,
  onOpenTextPad,
}: {
  canWrite: boolean;
  onUpload: () => void;
  onCreateFolder: () => void;
  onOpenTextPad: () => void;
}) {
  return (
    <Stack
      spacing={2}
      alignItems="center"
      sx={{
        marginTop: { xs: 20, md: 12 },
        color: (theme) => theme.palette.text.secondary,
      }}
    >
      <Typography>No files or folders</Typography>
      {canWrite && (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={onUpload}
          >
            Upload
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CreateNewFolderIcon />}
            onClick={onCreateFolder}
          >
            New folder
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<NoteAddIcon />}
            onClick={onOpenTextPad}
          >
            TextPad
          </Button>
        </Stack>
      )}
    </Stack>
  );
}

function PathBreadcrumb({
  path,
  onCwdChange,
}: {
  path: string;
  onCwdChange: (newCwd: string) => void;
}) {
  const parts = path.replace(/\/$/, "").split("/");

  return (
    <Breadcrumbs separator="/" sx={{ padding: 1 }}>
      <Button onClick={() => onCwdChange("")} sx={{ minWidth: 0, padding: 0 }}>
        <HomeIcon />
      </Button>
      {parts.map((part, index) =>
        index === parts.length - 1 ? (
          <Typography key={index} color="text.primary">
            {part}
          </Typography>
        ) : (
          <Link
            key={index}
            component="button"
            onClick={() => {
              onCwdChange(parts.slice(0, index + 1).join("/") + "/");
            }}
          >
            {part}
          </Link>
        )
      )}
    </Breadcrumbs>
  );
}

function DropZone({
  children,
  onDrop,
}: {
  children: React.ReactNode;
  onDrop: (files: FileList) => void;
}) {
  const [dragging, setDragging] = useState(false);

  return (
    <Box
      sx={{
        flexGrow: 1,
        overflowY: "auto",
        backgroundColor: (theme) => theme.palette.background.default,
        filter: dragging ? "brightness(0.9)" : "none",
        transition: "filter 0.2s",
      }}
      onDragEnter={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        onDrop(event.dataTransfer.files);
        setDragging(false);
      }}
    >
      {children}
    </Box>
  );
}

function Main({
  search,
  onError,
  session,
}: {
  search: string;
  onError: (error: Error) => void;
  session: Session;
}) {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up("md"));
  const [cwd, setCwd] = useState("");
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [multiSelected, setMultiSelected] = useState<string[] | null>(null);
  const [showUploadDrawer, setShowUploadDrawer] = useState(false);
  const [showTextPadDrawer, setShowTextPadDrawer] = useState(false);
  const [lastUploadKey, setLastUploadKey] = useState<string | null>(null);

  const transferQueue = useTransferQueue();
  const uploadEnqueue = useUploadEnqueue();
  const canWriteCwd = canWritePath(session, cwd);
  const canShowWriteControls = session.authenticated && (canWriteCwd || !cwd);

  const fetchFiles = useCallback((options?: { force?: boolean }) => {
    fetchPath(cwd, options)
      .then((files) => {
        setFiles(files);
        setMultiSelected(null);
      })
      .catch(onError)
      .finally(() => setLoading(false));
  }, [cwd, onError]);

  const refreshFiles = useCallback(() => {
    invalidatePath(cwd);
    fetchFiles({ force: true });
  }, [cwd, fetchFiles]);

  const uploadFromPicker = useCallback(() => {
    if (!canWriteCwd) return;

    const input = document.createElement("input");
    input.type = "file";
    input.accept = "*/*";
    input.multiple = true;
    input.onchange = () => {
      if (!input.files) return;
      uploadEnqueue(
        ...Array.from(input.files).map((file) => ({ file, basedir: cwd }))
      );
    };
    input.click();
  }, [canWriteCwd, cwd, uploadEnqueue]);

  const createFolderAndRefresh = useCallback(async () => {
    if (!(await createFolder(cwd))) return;
    invalidatePath(cwd);
    fetchFiles({ force: true });
  }, [cwd, fetchFiles]);

  useEffect(() => setLoading(true), [cwd]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  useEffect(() => {
    if (!transferQueue.length) return;
    const lastFile = transferQueue[transferQueue.length - 1];
    if (["pending", "in-progress"].includes(lastFile.status)) {
      setLastUploadKey(lastFile.remoteKey);
    } else if (lastUploadKey && lastFile.status === "completed") {
      invalidatePath(cwd);
      fetchFiles({ force: true });
      setLastUploadKey(null);
    } else if (lastUploadKey) {
      setLastUploadKey(null);
    }
  }, [cwd, fetchFiles, lastUploadKey, transferQueue]);

  const filteredFiles = useMemo(
    () =>
      (search
        ? files.filter((file) =>
            file.key.toLowerCase().includes(search.toLowerCase())
          )
        : files
      ).sort((a, b) => {
        if (isDirectory(a) !== isDirectory(b)) return isDirectory(a) ? -1 : 1;
        return a.key.localeCompare(b.key);
      }),
    [files, search]
  );

  const canManageSelection = useMemo(
    () =>
      Boolean(multiSelected?.length) &&
      multiSelected!.every((key) => canWritePath(session, key)),
    [multiSelected, session]
  );

  const handleMultiSelect = useCallback((key: string) => {
    setMultiSelected((prev) => {
      if (prev === null) return [key];
      if (prev.includes(key)) {
        const updated = prev.filter((k) => k !== key);
        return updated.length ? updated : null;
      }
      return [...prev, key];
    });
  }, []);

  const fileGrid = (
    <FileGrid
      files={filteredFiles}
      onCwdChange={(newCwd: string) => setCwd(newCwd)}
      multiSelected={multiSelected}
      onMultiSelect={handleMultiSelect}
      emptyMessage={
        <EmptyState
          canWrite={canWriteCwd}
          onUpload={uploadFromPicker}
          onCreateFolder={createFolderAndRefresh}
          onOpenTextPad={() => setShowTextPadDrawer(true)}
        />
      }
      puburl={session.puburl}
    />
  );

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          minHeight: 48,
          paddingX: 1,
        }}
      >
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {cwd ? (
            <PathBreadcrumb path={cwd} onCwdChange={setCwd} />
          ) : (
            <Button
              onClick={() => setCwd("")}
              sx={{ minWidth: 0, padding: 0.5 }}
            >
              <HomeIcon />
            </Button>
          )}
        </Box>
        {desktop && (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={refreshFiles}
            >
              Refresh
            </Button>
            {canShowWriteControls && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CreateNewFolderIcon />}
                  onClick={createFolderAndRefresh}
                >
                  New folder
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<NoteAddIcon />}
                  disabled={!canWriteCwd}
                  onClick={() => setShowTextPadDrawer(true)}
                >
                  TextPad
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<UploadIcon />}
                  disabled={!canWriteCwd}
                  onClick={uploadFromPicker}
                >
                  Upload
                </Button>
              </>
            )}
          </Stack>
        )}
      </Stack>

      {loading ? (
        <Centered>
          <CircularProgress />
        </Centered>
      ) : canWriteCwd ? (
        <DropZone
          onDrop={(files) => {
            uploadEnqueue(
              ...Array.from(files).map((file) => ({ file, basedir: cwd }))
            );
          }}
        >
          {fileGrid}
        </DropZone>
      ) : (
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            backgroundColor: (theme) => theme.palette.background.default,
          }}
        >
          {fileGrid}
        </Box>
      )}

      {!desktop && multiSelected === null && canShowWriteControls && (
        <>
          <UploadFab onClick={() => setShowUploadDrawer(true)} />
          {canWriteCwd && (
            <Button
              variant="contained"
              startIcon={<NoteAddIcon />}
              sx={{
                position: "fixed",
                bottom: 90,
                right: 24,
                zIndex: 999,
              }}
              onClick={() => setShowTextPadDrawer(true)}
            >
              Open TextPad
            </Button>
          )}
        </>
      )}

      <UploadDrawer
        open={showUploadDrawer}
        setOpen={setShowUploadDrawer}
        cwd={cwd}
        onUpload={refreshFiles}
        canUpload={canWriteCwd}
      />

      <TextPadDrawer
        open={showTextPadDrawer}
        setOpen={setShowTextPadDrawer}
        cwd={cwd}
      />

      <MultiSelectToolbar
        multiSelected={multiSelected}
        onClose={() => setMultiSelected(null)}
        onDownload={() => {
          if (multiSelected?.length !== 1) return;
          const a = document.createElement("a");
          a.href = fileUrl(multiSelected[0], session.puburl);
          a.download = multiSelected[0].split("/").pop()!;
          a.click();
        }}
        onRename={async () => {
          if (multiSelected?.length !== 1) return;
          const newName = window.prompt("Rename to:");
          if (!newName) return;
          const cleanName = newName.trim();
          if (!validName(cleanName)) {
            window.alert("Invalid file name");
            return;
          }

          const target = joinKey(cwd, cleanName);
          if (!canWritePath(session, multiSelected[0])) {
            window.alert("You do not have permission to rename this item.");
            return;
          }
          if (!canWritePath(session, target)) {
            window.alert("You do not have permission to write the new path.");
            return;
          }

          try {
            await copyPaste(multiSelected[0], target, true);
            invalidatePath(cwd);
            fetchFiles({ force: true });
          } catch (error) {
            onError(error instanceof Error ? error : new Error("Rename failed"));
          }
        }}
        onDelete={async () => {
          if (!multiSelected?.length) return;
          if (!multiSelected.every((key) => canWritePath(session, key))) {
            window.alert("You do not have permission to delete these items.");
            return;
          }
          const filenames = multiSelected
            .map((key) => key.replace(/\/$/, "").split("/").pop())
            .join("\n");
          const confirmMessage = "Delete the following file(s) permanently?";
          if (!window.confirm(`${confirmMessage}\n${filenames}`)) return;

          try {
            const response = await fetch("/api/delete", {
              method: "POST",
              headers: authHeaders({ "Content-Type": "application/json" }),
              body: JSON.stringify({ keys: multiSelected }),
            });
            if (!response.ok) throw new Error(await response.text());
            invalidatePath(cwd);
            fetchFiles({ force: true });
          } catch (error) {
            onError(error instanceof Error ? error : new Error("Delete failed"));
          }
        }}
        onShare={() => {
          if (multiSelected?.length !== 1) return;
          const url = new URL(
            fileUrl(multiSelected[0], session.puburl),
            window.location.href
          ).toString();

          if (navigator.share) {
            navigator.share({ url });
          } else {
            navigator.clipboard?.writeText(url);
          }
        }}
        canDelete={canManageSelection}
        canRename={canManageSelection}
      />
    </>
  );
}

export default Main;
